import React, { useState, useEffect, useMemo, useCallback, FC } from 'react';
import {
  Button,
  Card,
  CardHeader,
  CardPreview,
  Dropdown,
  Option,
  Table,
  TableBody,
  TableCell,
  TableCellLayout,
  TableHeader,
  TableHeaderCell,
  TableRow,
  Spinner,
  makeStyles,
  tokens,
  Body1,
  Title3,
  Caption1,
  Title1,
  ProgressBar,
  ToggleButton,
  Tooltip,
  Text,
  Dialog,
  DialogSurface,
  DialogBody,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@fluentui/react-components';
import {
  SendRegular,
  EyeRegular,
  OpenRegular,
  ArrowUndoRegular,
  AddRegular,
  InfoRegular,
  CheckmarkCircleRegular,
} from '@fluentui/react-icons';

import { Module } from '../models/module';
import { Tendering, TenderingFeeReportPeriod, TenderFeePeriodReport, Tender, TenderFeeStatuses, ReportPeriodStates, TenderingFee } from '../models/tendering';
import { UserSystemData } from '../models/users';
import { SystemOption } from '../models/systemOptions';
import { getTenderingDocument, getTendersDocumentFilteredAnswers, getTendersDocumentFilteredAnswersByIDs, updateTenderDocument, updateTenderFeePeriodReport, updateTenderingDocument } from '../services/tenderingService';
import { useAppContext } from '../AppContext';
import { getSystemOptionsByID } from '../services/systemOptionService';
import moment from 'moment';
import { deepCloneObject } from '../utils/clone';
import { getOngoingReportPeriod } from '../utils/fee-report-utils';
import { postExternal } from '../services/externalService';
import TenderSettingsDrawer from './TenderSettings/TenderSettingsDrawer';
import ReportDialog from './dialogs/ReportDialog';
import { RoleDTO } from '../models/role';

export const TenderingFeeTool: FC = () => {
  
  // State
  const [modules, setModules] = useState<Module[]>([]);
  const [selectedTendering, setSelectedTendering] = useState<Module | null>(null);
  const [selectedPeriod, setSelectedPeriod] = useState<TenderingFeeReportPeriod | null>(null);
  const [tendering, setTendering] = useState<Tendering | null>(null);
  const [tenders, setTenders] = useState<Tender[]>([]);
  const [tenderingPeriods, setTenderingPeriods] = useState<TenderingFeeReportPeriod[]>([]);
  const [tenderReports, setTenderReports] = useState<TenderFeePeriodReport[]>([]);
  const [loading, setLoading] = useState(false);
  const {user, featurePermissions} = useAppContext();
  const [systemOption, setSystemOption] = useState<SystemOption | null>(null);
  const [userTenders, setUserTenders] = useState<Module[]>([]);
  const [isDrawerOpen, setIsDrawerOpen] = useState<boolean>(false);
  const [selectedTender, setSelectedTender] = useState<Tender | null>(null);
  const [isReportDialogOpen, setIsReportDialogOpen] = useState<boolean>(false);
  const [selectedReport, setSelectedReport] = useState<TenderFeePeriodReport | null>(null);
  const [isSuccessDialogOpen, setIsSuccessDialogOpen] = useState<boolean>(false);

  const tenderingProperties = ["content.tenderingFeeReports", "content.tenderingContractFee", "content.tenderingParts", "content.properties"];
  const tenderProperties = ["content.reportPeriods"];

  useEffect(() => {
    const initializeData = async () => {
      try {
        const fetchedModules: Module[] = user.modules;
        const tenderingModules = fetchedModules.filter(m => m.moduleType === 'Tendering');
        
        if (user.roles.some((role: any) => role.roleType !== 'General')) {
          console.log(featurePermissions);
          const userTenderModules = fetchedModules.filter(m => m.moduleType === 'Tender' && featurePermissions?.some(fp => (fp.moduleID === m.moduleID && fp.action == "Tendering fee tool page")));
          setModules(tenderingModules.filter(tm => userTenderModules.some(utm => utm.modulePath?.split('_')[2] === tm.moduleID.toString())));
          setUserTenders(userTenderModules);
        }

        if (user.roles.some((role: any) => role.roleType === 'General')) {
          const systemOptions = await getSystemOptionsByID(1);
          setSystemOption(systemOptions);
          setModules(tenderingModules);
        }
      } catch (error) {
        console.error(error);
      }
    };

    initializeData();
  }, [user]);

  const onTenderingChange = async (moduleId: string) => {
    const module = modules.find(m => m.moduleID.toString() === moduleId);
    if (!module) return;

    setSelectedTendering(module);
    setSelectedPeriod(null);
    setLoading(true);

    try {
      const tenderingData = await getTenderingDocument(module.moduleID, tenderingProperties);
      setTendering(tenderingData);
      setTenderingPeriods(tenderingData.tenderingFeeReports?.sort((a, b) => new Date(b.periodStart).getTime() - new Date(a.periodStart).getTime()) || []);

      const questionsID = tenderingData.properties?.questionMappings?.billingNr;
      
      let tenderData: Tender[];
      if (userTenders.length > 0) {
        const filteredIds = filterTendersByTendering(module.moduleID);
        tenderData = await getTendersDocumentFilteredAnswersByIDs(
          filteredIds,
          tenderProperties,
          questionsID ? [questionsID] : []
        );
      } else {
        tenderData = await getTendersDocumentFilteredAnswers(
          module.moduleID,
          tenderProperties,
          questionsID ? [questionsID] : []
        );
      }

      setTenders(tenderData);

      if (!tenderingData.tenderingFeeReports && tenderingData.tenderingContractFee) {
        await createPeriods(tenderingData, tenderData);
      }

      setReportStatus(tenderingData, tenderData);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const filterTendersByTendering = (tenderingID: number): number[] => {
    return userTenders
      .filter(m => parseInt(m.modulePath?.split('_')[2] || '0') === tenderingID)
      .map(m => m.moduleID);
  };

  const createReportPeriods = (tenderingData: Tendering, tenderData: Tender[]) => {
    const fee = tenderingData.tenderingContractFee!;
    const reportPeriods: TenderingFeeReportPeriod[] = [];

    let month = 0;
    const year = new Date().getFullYear();
    let firstPeriodStart = moment(`${year}-02-01`, 'YYYY-MM-DD').add(fee.startDay - 1, 'days');
    let interval = 12/fee.reportPeriodInterval;
    
    for (let i = 0; i < fee.reportPeriodInterval; i++) {
      let periodStart = moment(firstPeriodStart).add(month, 'months');
      let periodEnd = moment(periodStart).add(fee.nrOfDaysReportOpen, 'days');
      let billingDate = moment(periodEnd).add(fee.billingDay, 'days');
      reportPeriods.push({
        reportID: `${tenderingData.tenderingID}-${periodStart.format('L')}-${i}`,
        tenderingID: tenderingData.tenderingID,
        periodStart: periodStart.format('L'),
        periodEnd: periodEnd.format('L'),
        notifyDate: moment(periodStart).add(fee.notifyDay, 'days').format('L'),
        reminderStartDate: moment(periodStart).add(fee.reminderDay, 'days').format('L'),
        billingDate: billingDate.format('L'),
        header: `Inbetalning ${year} - ${i + 1}`,
        deadlineDate: moment(billingDate).add(fee.deadline, 'days').format('L'),
        state: 'Planned',
        status: `0/${tenderData.length}` 
      });
      month = month + interval;
    }

    const startMonth = new Date(fee.feeStartDate).getMonth();
    if (startMonth > 1) {
      reportPeriods.splice(0, startMonth);
    }

    // Create tender reports for each period
    tenderData.forEach(tender => {
      const periods: TenderFeePeriodReport[] = [];
      reportPeriods.forEach(rp => {
        periods.push({
          reportID: rp.reportID,
          tenderID: tender.tenderID,
          tender: tender.tenderName,
          status: TenderFeeStatuses.Notified.status,
          nextState: TenderFeeStatuses.Notified.next,
          payed: 0,
          sent: false,
          report: [],
          owed: 0
        });
      });

      if (tender.reportPeriods) {
        tender.reportPeriods = [...tender.reportPeriods, ...periods];
      } else {
        tender.reportPeriods = periods;
      }
    });

    return reportPeriods;
  };

  const createPeriods = async (tenderingData?: Tendering, tenderData?: Tender[]) => {
    const currentTendering = tenderingData || tendering;
    const currentTenders = tenderData || tenders;
    
    if (!currentTendering || !currentTenders) return;

    setLoading(true);
    
    const reportPeriods = createReportPeriods(currentTendering, currentTenders);
    
    if (currentTendering.tenderingFeeReports) {
      currentTendering.tenderingFeeReports = [...currentTendering.tenderingFeeReports, ...reportPeriods];
    } else {
      currentTendering.tenderingFeeReports = reportPeriods;
    }

    try {
      await updateTenderingDocument(currentTendering.tenderingID, currentTendering);
      
      const updatePromises = currentTenders.map(t => {
        const tenderClone = deepCloneObject(t);
        delete (tenderClone as any).tenderAnswers;
        return updateTenderDocument(t.tenderID, tenderClone);
      });

      await Promise.all(updatePromises);
      
      if (!tenderingData) {
        setTendering(currentTendering);
        setTenderingPeriods(currentTendering.tenderingFeeReports);
        setTenders(currentTenders);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const deleteContractFeeAndReports = async () => {
    if (!tendering || !tenders) return;

    const updatedTendering = deepCloneObject(tendering);
    updatedTendering.tenderingContractFee = undefined;
    updatedTendering.tenderingFeeReports = undefined;

    const updatedTenders = tenders.map(tender => {
      const tenderClone = deepCloneObject(tender);
      tenderClone.reportPeriods = undefined;
      return tenderClone;
    });

    setLoading(true);

    try {
      await updateTenderingDocument(updatedTendering.tenderingID, updatedTendering);
      
      const updatePromises = updatedTenders.map(t => 
        updateTenderDocument(t.tenderID, t)
      );

      await Promise.all(updatePromises);
      
      setTendering(updatedTendering);
      setTenderingPeriods([]);
      setTenders(updatedTenders);
      setSelectedPeriod(null);
      setTenderReports([]);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const selectPeriod = (period: TenderingFeeReportPeriod) => {
    setSelectedPeriod(period);
    const tenderReportsForPeriod: TenderFeePeriodReport[] = [];
    
    tenders.forEach(tender => {
      const report = tender.reportPeriods?.find(r => r.reportID === period.reportID);
      if (report) {
        tenderReportsForPeriod.push(report);
      }
    });

    tenderReportsForPeriod.sort((a, b) => a.tender.localeCompare(b.tender, 'sv'));
    setTenderReports(tenderReportsForPeriod);
  };

  const setReportStatus =(tenderingData: Tendering, tenderData: Tender[]) => {
    const reportPeriods: TenderFeePeriodReport[] = [];
    tenderData.forEach(t => {
      if (t.reportPeriods) {
        reportPeriods.push(...t.reportPeriods);
      }
    });

    if (tenderingData.tenderingFeeReports) {
      tenderingData.tenderingFeeReports.forEach(r => {
        const ongoingPeriod = getOngoingReportPeriod(tenderingData);
        if (ongoingPeriod && ongoingPeriod.reportID === r.reportID) {
          r.state = 'Notified';
        }

        if (new Date() > new Date(r.deadlineDate)) {
          const unpaidReports = reportPeriods.filter(rp => 
            rp.reportID === r.reportID && rp.status !== TenderFeeStatuses.Payed.status
          );
          if (unpaidReports.length > 0) {
            r.state = 'Overdue';
          } else {
            r.state = 'Ended';
          }
        } else {
          const allPaidReports = reportPeriods.filter(rp => 
            rp.reportID === r.reportID && rp.status === TenderFeeStatuses.Payed.status
          );
          if (allPaidReports.length === reportPeriods.filter(rp => rp.reportID === r.reportID).length) {
            r.state = 'Payed';
          }
        }

        const periods = reportPeriods.filter(rp => rp.reportID === r.reportID);
        const total = periods.length.toString();
        const payed = periods.filter(p => 
          p.status === TenderFeeStatuses.Reported.status
          || p.status === TenderFeeStatuses.Billed.status 
          || p.status === TenderFeeStatuses.Payed.status
          || p.status === TenderFeeStatuses.Overdue.status
        ).length.toString(); //add OR reported
        r.status = `${payed}/${total}`;
      });
    }
  };

  const sendBill = async (report: TenderFeePeriodReport) => {
    if (!tendering || !systemOption) return;

    const billingNrID = tendering.properties?.questionMappings?.billingNr;
    const tender = tenders.find(t => t.tenderID === report.tenderID);
    
    if (!tender || !billingNrID) return;

    const billingNumber = tender.tenderAnswers?.find(a => a.questionID === billingNrID)?.value;
    
    const billObject = {
      tenderingContractFee: tendering.tenderingContractFee,
      report,
      reportPeriod: tendering.tenderingFeeReports?.find(r => r.reportID === report.reportID),
      billingNumber,
    };

    report.status = TenderFeeStatuses.Billed.status;
    report.nextState = TenderFeeStatuses.Billed.next;

    try {
      await postExternal(systemOption.billingLink, billObject);
      await updateTenderFeePeriodReport(report);
      
      // Update local state
      setTenderReports(prev => 
        prev.map(r => r.reportID === report.reportID && r.tenderID === report.tenderID ? report : r)
      );
    } catch (error) {
      console.error(error);
      report.status = TenderFeeStatuses.Reported.status;
      report.nextState = TenderFeeStatuses.Reported.next;
    }
  };

  const sendAllBills = async () => {
    const reportedReports = tenderReports.filter(r => r.status === 'Inrapporterat');
    
    for (const report of reportedReports) {
      await sendBill(report);
    }
  };

  const disableAllBillsButton = useMemo(() => {
    const reportedReports = tenderReports.filter(r => r.status === 'Inrapporterat');
    return reportedReports.length < 1;
  }, [tenderReports]);


  const openTender = (id: number) => {
    const tender = tenders.find(t => t.tenderID === id);
    setSelectedTender(tender || null);
    setIsDrawerOpen(true);
  }

  const openReportDialog = (row: TenderFeePeriodReport): void => {
    setSelectedReport(row);
    setIsReportDialogOpen(true);
  };

  const convertToMonthName = (text: string): string => {
    const monthNames = ['Januari', 'Februari', 'Mars', 'April', 'Maj', 'Juni', 'Juli', 'Augusti', 'September', 'Oktober', 'November', 'December'];
    
    const regex = /- (\d{1,2})/;
    const match = text.match(regex);
    
    if (match) {
      return text.replace(match[0], ' - ' + monthNames[parseInt(match[1]) - 1]);
    }
    
    return text;
  };

  const convertToMonthNumber = (monthName: string): string => {
    const months = [{
      name: 'Januari',
      number: '01'
    }, 
    {
      name: 'Februari',
      number: '02'
    }, 
    {
      name: 'Mars',
      number: '03'
    }, 
    {
      name: 'April',
      number: '04'
    }, 
    {
      name: 'Maj',
      number: '05'
    }, 
    {
      name: 'Juni',
      number: '06'
    }, 
    {
      name: 'Juli',
      number: '07'
    }, 
    {
      name: 'Augusti',
      number: '08'
    }, 
    {
      name: 'September',
      number: '09'
    }, 
    {
      name: 'Oktober',
      number: '10'
    }, 
    {
      name: 'November',
      number: '11'
    }, 
    {
      name: 'December',
      number: '12'
    }]

    return months.find(m => m.name === monthName)?.number || '01';
  }
 
  const calculateOwed = (reportPeriod: TenderFeePeriodReport): number => {
    let sum = 0;
    reportPeriod.report?.forEach(report => {
      if (report.value) {
        sum += report.value * (report.measure.feeValue * 0.01);
      }
    });
    return Number(sum.toFixed(2));
  };

  const saveReport = (r: TenderFeePeriodReport, sendBill: boolean) => {
    let tenderToUpdate = tenders.find(t => t.tenderID === r.tenderID);

    if(sendBill){
      r.status = TenderFeeStatuses.Reported.status;
      r.nextState = TenderFeeStatuses.Reported.next;
      r.sent = true;
    }

    //Find the tender and update the report period
    if (tenderToUpdate?.reportPeriods) {
      r.owed = calculateOwed(r);
      tenderToUpdate.reportPeriods = tenderToUpdate.reportPeriods.map(rp => rp.reportID === r.reportID && rp.tenderID === r.tenderID ? r : rp);
    }

    //Update local state of the reports
    let reportsToUpdate = tenderReports.map(tr => tr.reportID === r.reportID && tr.tenderID === r.tenderID ? r : tr);
    setTenderReports(reportsToUpdate);
    const newTenders = tenders.map(t => t.tenderID === tenderToUpdate?.tenderID ? tenderToUpdate : t) as Tender[];
    setTenders(newTenders);

    // Update the X/Y status on the period when a report is submitted
    if (sendBill && tendering) {
      const total = newTenders.filter(t => t.reportPeriods?.some(rp => rp.reportID === r.reportID)).length;
      const reported = newTenders.filter(t => t.reportPeriods?.find(rp => rp.reportID === r.reportID)?.sent).length;
      const updatedPeriods = tenderingPeriods.map(p =>
        p.reportID === r.reportID ? { ...p, status: `${reported}/${total}` } : p
      );
      setTenderingPeriods(updatedPeriods);

      const updatedTendering = deepCloneObject(tendering);
      updatedTendering.tenderingFeeReports = updatedPeriods;
      updateTenderingDocument(updatedTendering.tenderingID, updatedTendering);
    }

    let updatedTender = deepCloneObject(tenderToUpdate);
    
    delete updatedTender?.tenderAnswers;
    updateTenderDocument(updatedTender!.tenderID, updatedTender);

    closeReportDialog();
    if (sendBill) {
      setIsSuccessDialogOpen(true);
    }
  };

  const closeReportDialog = () => {
    setIsReportDialogOpen(false); 
    setSelectedReport(null);
  }

  const getDateRangeString = (header: string): string => {
    const stringArray = header.split(' ');

    let year1: number = 0;
    let year2: number = 0;
    let month1: number = 0;
    let month2: number = 0;

    if(stringArray.length == 4){
      year1 = parseInt(stringArray[1]);
      month1 = parseInt(stringArray[3]);
    }
    else{
      year1 = parseInt(stringArray[3]);
      month1 = parseInt(stringArray[2]);
      year2 = parseInt(stringArray[6]);
      month2 = parseInt(convertToMonthNumber(stringArray[5]));
    }

    const shortYear = year1.toString().slice(-2);
    const shortYear2 = year2 !== 0 ? year2.toString().slice(-2) : year1.toString().slice(-2);
    const paddedMonth = month1.toString().padStart(2, '0');
    const paddedMonth2 = month2 !== 0 ? month2.toString().padStart(2, '0') : paddedMonth;
    const lastDay = new Date(year2 !== 0 ? year2 : year1, month2 !== 0 ? month2 : month1, 0).getDate();
    const paddedLastDay = lastDay.toString().padStart(2, '0');
    
    return `${shortYear}${paddedMonth}01-${shortYear2}${paddedMonth2}${paddedLastDay}`;
  };

  return (
    <div className='bg-[#ebebeb]'>
      <div>
        <div className="h-[130px] bg-gradient-to-r rounded from-orange-700 to-yellow-500">

        </div>
        <div className="bg-white w-full max-w-[1480px] m-auto flex flex-col gap-5 pt-9 p-5 rounded -mt-20">
          <Title1>Inrapportering för avtalsadministrativ avgift</Title1>
          <div className="flex flex-row gap-2 flex-wrap">
            {modules.map((module) => (
              <ToggleButton
                checked={selectedTendering?.moduleID === module.moduleID}
                appearance={selectedTendering?.moduleID === module.moduleID ? 'primary' : 'outline'}
                disabled={loading} 
                key={module.moduleID} 
                onClick={() => onTenderingChange(module.moduleID.toString())}>
                  {module.moduleName}
              </ToggleButton>
            ))}
          </div>
          
          {loading && (<ProgressBar />)}
        </div>

        {loading && (  
        <div className='mt-10'>
          <Spinner label="Laddar..." />
        </div>)}

        <div className='flex flex-row w-full max-w-[1480px] mt-5 m-auto gap-5'>
          {selectedTendering && !loading && (
            <div className='bg-white w-1/3 rounded p-5'>
              <Title3>{tendering?.tenderingName}</Title3>
              <Table size="small" aria-label="Table with small size">
                <TableHeader>
                  <TableRow>
                    <TableHeaderCell className="w-32 lg:w-40 xl:w-48 2xl:w-56">
                      <Text weight='semibold'>Rubrik</Text>
                    </TableHeaderCell>
                    {user.roles.some((role: RoleDTO) => role.roleType === 'General') && <TableHeaderCell className="w-16 lg:w-20 xl:w-24 2xl:w-28">
                      <Text weight='semibold'>Status</Text>
                    </TableHeaderCell>}
                    <TableHeaderCell className="w-20 lg:w-24 xl:w-28 2xl:w-32">
                      <Text weight='semibold'>Intäkter mellan</Text>
                    </TableHeaderCell>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {tenderingPeriods.map((period) => (
                    <TableRow
                      key={period.reportID}
                      className='cursor-pointer'
                      onClick={() => selectPeriod(period)}
                    >
                      <TableCell>
                        <TableCellLayout>
                          {convertToMonthName(period.header)}
                        </TableCellLayout>
                      </TableCell>
                      {user.roles.some((role: RoleDTO) => role.roleType === 'General') &&<TableCell>
                        <TableCellLayout>
                          {period.status}
                        </TableCellLayout>
                      </TableCell>}
                      <TableCell>
                        <TableCellLayout>
                          {getDateRangeString(period.header)}
                        </TableCellLayout>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
          {selectedPeriod && !loading && (
            <div className='bg-white w-2/3 rounded p-5'>
              <div className='flex justify-between align-center mb-2'>
                <Title3>{convertToMonthName(selectedPeriod.header)}</Title3>
                {user.roles.some((role: any) => role.roleType === 'General') && <Button
                  disabled={disableAllBillsButton}
                  onClick={sendAllBills}
                >
                  Skicka alla inrapporterade till fakturor
                </Button>}
              </div>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHeaderCell className="w-full lg:w-2/3 xl:w-1/2">
                      <Text weight='semibold'>
                        Leverantör
                      </Text>
                    </TableHeaderCell>
                    <TableHeaderCell className="w-16 lg:w-20 xl:w-24">
                      <Text weight='semibold'>
                        Status
                      </Text>
                    </TableHeaderCell>
                    <TableHeaderCell className="w-16 lg:w-20 xl:w-24">
                      <Text weight='semibold'>
                        Datum inrapp.
                      </Text>
                    </TableHeaderCell>
                    <TableHeaderCell className="w-16 lg:w-20 xl:w-24">
                      <Text weight='semibold'>
                        Ackumulerat
                      </Text>
                    </TableHeaderCell>
                    <TableHeaderCell className="w-40 xl:w-44"></TableHeaderCell>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {tenderReports.map((report) => (
                    <TableRow className='group cursor-pointer' key={`${report.tenderID}-${report.reportID}`}>
                      <TableCell>
                        <TableCellLayout><Text weight='semibold'>{report.tender}</Text></TableCellLayout>
                      </TableCell>
                      <TableCell>
                        <TableCellLayout>{report.status}</TableCellLayout>
                      </TableCell>
                      <TableCell>
                        {report.status !== 'Notifierad' && <TableCellLayout>{(report.report 
                          && report.report[0] 
                          && report.report[0].user.lastActivity) 
                          ? new Date(report.report[0].user.lastActivity).toLocaleDateString("sv-SE") : ""}
                        </TableCellLayout>}
                      </TableCell>
                      <TableCell>
                        <TableCellLayout>
                          {calculateOwed(report)}
                        </TableCellLayout>
                      </TableCell>
                      <TableCell>
                        <TableCellLayout>
                          <div className='flex flex-row gap-1'>
                            {report.status === TenderFeeStatuses.Reported.status && user.roles.some((role: any) => role.roleType === 'General') && (
                              <Tooltip content='Skicka faktura' relationship='label'>
                                <Button
                                  className="opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                                  appearance="primary"
                                  shape='circular'
                                  icon={<SendRegular />}
                                  title="Skicka faktura"
                                  onClick={() => sendBill(report)}
                                  />
                              </Tooltip>
                            )}
                            {report.status === TenderFeeStatuses.Notified.status ? (
                              <Button
                                className="opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap shrink-0"
                                appearance="primary"
                                size="small"
                                icon={<SendRegular />}
                                onClick={() => openReportDialog(report)}
                              >
                                Skicka in rapport
                              </Button>
                            ) : (
                              <Tooltip content='Granska rapport' relationship='label'>
                                <Button
                                  className="opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                                  appearance="primary"
                                  shape='circular'
                                  icon={<EyeRegular />}
                                  onClick={() => openReportDialog(report)}
                                  />
                              </Tooltip>
                            )}
                            <Tooltip content='Visa verksamhet' relationship='label'>
                              <Button
                                className="opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                                icon={<InfoRegular />}
                                shape='circular'
                                onClick={() => openTender(report.tenderID)}   
                              />
                            </Tooltip>
                          </div>
                        </TableCellLayout>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
      </div>
      <TenderSettingsDrawer
        open={isDrawerOpen}
        tab='info'
        onClose={() => setIsDrawerOpen(false)}
        partialTender={selectedTender}
      />
      <Dialog open={isSuccessDialogOpen} onOpenChange={() => setIsSuccessDialogOpen(false)}>
        <DialogSurface>
          <DialogBody>
            <DialogTitle>
              <div className="flex items-center gap-2">
                <CheckmarkCircleRegular style={{ color: 'green', fontSize: '24px' }} />
                Rapport inskickad
              </div>
            </DialogTitle>
            <DialogContent>
              Rapporten har skickats in.
            </DialogContent>
            <DialogActions>
              <Button appearance="primary" onClick={() => setIsSuccessDialogOpen(false)}>Stäng</Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
      {selectedReport && tendering?.tenderingContractFee && <ReportDialog
        open={isReportDialogOpen}
        onClose={closeReportDialog}
        feePeriodReport={selectedReport}
        tenderingFee={tendering?.tenderingContractFee}
        saveReport={saveReport}
        tenderingName={tendering.tenderingName}
        notifyText={tendering.tenderingContractFee.notifyText}
      />}
    </div>
  );
};

export default TenderingFeeTool;
